import React, { useCallback, useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

function ClientEditModal({ client, fetchCustomers, isOpen, onClose }) {
  console.log('ClientEditModal render:', { client, isOpen });

  const { t } = useTranslation('registry');
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressState, setAddressState] = useState('');
  const [isValidCp, setIsValidCp] = useState(true);

  const cfdiUseOptions = [
    { label: t('commerce.modal.cfdi.G01'), value: 'G01' },
    { label: t('commerce.modal.cfdi.G02'), value: 'G02' },
    { label: t('commerce.modal.cfdi.G03'), value: 'G03' },
    { label: t('commerce.modal.cfdi.D01'), value: 'D01' },
    { label: t('commerce.modal.cfdi.D02'), value: 'D02' },
    { label: t('commerce.modal.cfdi.D03'), value: 'D03' },
    { label: t('commerce.modal.cfdi.D04'), value: 'D04' },
    { label: t('commerce.modal.cfdi.D05'), value: 'D05' },
    { label: t('commerce.modal.cfdi.D06'), value: 'D06' },
    { label: t('commerce.modal.cfdi.D07'), value: 'D07' },
    { label: t('commerce.modal.cfdi.P01'), value: 'P01' }
  ];

  const taxRegimeOptions = [
    { label: t('commerce.modal.taxRegime.626'), value: 626 },
    { label: t('commerce.modal.taxRegime.601'), value: 601 },
    { label: t('commerce.modal.taxRegime.612'), value: 612 },
    { label: t('commerce.modal.taxRegime.621'), value: 621 },
    { label: t('commerce.modal.taxRegime.603'), value: 603 },
    { label: t('commerce.modal.taxRegime.622'), value: 622 }
  ];

  // Define schemas per tab
  const getValidationSchema = tab => {
    switch (tab) {
      case 0: // General
        return Yup.object({
          address: Yup.object({
            address_line_1: Yup.string().max(100).required(t('commerce.registrationForm.required')),
            city: Yup.string().max(45).required(t('commerce.registrationForm.required')),
            neighborhood: Yup.string().max(45).required(t('commerce.registrationForm.required')),
            postal_code: Yup.string().max(5).required(t('commerce.registrationForm.required'))
          }),
          name: Yup.string().max(100).required(t('commerce.registrationForm.required'))
        });
      case 1: // Fiscal
        return Yup.object({
          tax_info: Yup.object({
            cfdi_use_code: Yup.string().required(t('commerce.registrationForm.required')),
            postal_code: Yup.string().max(5).required(t('commerce.registrationForm.required')),
            rfc: Yup.string().max(14).required(t('commerce.registrationForm.required')),
            tax_name: Yup.string().max(100).required(t('commerce.registrationForm.required')),
            tax_regime: Yup.number().required(t('commerce.registrationForm.required'))
          })
        });
      case 2: // Contact
        return Yup.object({
          bc_email: Yup.string().email().max(100).required(t('commerce.registrationForm.required')),
          bc_first_name: Yup.string().max(45).required(t('commerce.registrationForm.required')),
          bc_last_name: Yup.string().max(45).required(t('commerce.registrationForm.required')),
          category: Yup.string().required(t('commerce.registrationForm.required'))
        });
      case 3: // Commercial
        return Yup.object({
          number_of_access_points: Yup.number().required(t('commerce.registrationForm.required')),
          number_of_bridges: Yup.number().required(t('commerce.registrationForm.required')),
          number_of_users: Yup.number().required(t('commerce.registrationForm.required')),
          plan: Yup.string().required(t('commerce.registrationForm.required')),
          price_per_user: Yup.number().required(t('commerce.registrationForm.required'))
        });
      default:
        return Yup.object({});
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      address: {
        address_line_1: client?.address?.address_line_1 || '',
        address_line_2: client?.address?.address_line_2 || '',
        city: client?.address?.city || '',
        country: client?.address?.country || 'MEX',
        country_code: client?.address?.country_code || 'MEX',
        neighborhood: client?.address?.neighborhood || '',
        postal_code: client?.address?.postal_code || '',
        state: client?.address?.state || ''
      },
      bc_email: client?.bc_email || '',
      bc_first_name: client?.bc_first_name || '',
      bc_is_oc:
        (client?.bc_first_name &&
          client.bc_first_name === client.oc_first_name &&
          client.bc_last_name === client.oc_last_name &&
          client.bc_email === client.oc_email) ||
        false,
      bc_last_name: client?.bc_last_name || '',
      business_partner_id: client?.business_partner_id || '232bfb09-0dc2-4a04-a921-b879f8158b8e',
      category: client?.category || '',
      inPersonVisit: client?.stage !== 'prospect',
      name: client?.name || '',
      number_of_access_points: client?.quotes?.[0]?.number_of_access_points || '',
      number_of_bridges: client?.quotes?.[0]?.number_of_bridges || '',
      number_of_users: client?.quotes?.[0]?.number_of_users || '',
      oc_email: client?.oc_email || '',
      oc_first_name: client?.oc_first_name || '',
      oc_last_name: client?.oc_last_name || '',
      plan: client?.quotes?.[0]?.plan_id ? client.quotes[0].plan_id : client?.quotes?.[0]?.plan?.plan_id || '',
      price_per_user: client?.quotes?.[0]?.price_per_user || '',
      tax_info: {
        cfdi_use_code: client?.tax_info?.cfdi_use_code || '',
        postal_code: client?.tax_info?.postal_code || '',
        rfc: client?.tax_info?.rfc || '',
        tax_name: client?.tax_info?.tax_name || '',
        tax_regime: client?.tax_info?.tax_regime ? parseInt(client.tax_info.tax_regime, 10) : ''
      }
    },
    onSubmit: async values => {
      setIsSubmitting(true);
      try {
        let payload = {};

        // Only send fields relevant to the active tab
        if (activeTab === 0) {
          // General
          console.log('🔍 Frontend - values.address:', values.address);
          console.log('🔍 Frontend - values.address.state:', values.address.state);
          payload = {
            address: values.address,
            inPersonVisit: values.inPersonVisit,
            name: values.name
          };
          console.log('🔍 Frontend - payload being sent:', payload);
        } else if (activeTab === 1) {
          // Fiscal
          payload = {
            tax_info: {
              ...values.tax_info,
              tax_regime: values.tax_info.tax_regime.toString()
            }
          };
        } else if (activeTab === 2) {
          // Contact
          payload = {
            bc_email: values.bc_email,
            bc_first_name: values.bc_first_name,
            bc_is_oc: values.bc_is_oc,
            bc_last_name: values.bc_last_name,
            category: values.category,
            oc_email: values.oc_email,
            oc_first_name: values.oc_first_name,
            oc_last_name: values.oc_last_name
          };
        } else if (activeTab === 3) {
          // Commercial
          payload = {
            number_of_access_points: values.number_of_access_points,
            number_of_bridges: values.number_of_bridges,
            number_of_users: values.number_of_users,
            plan_id: values.plan, // Send as plan_id
            price_per_user: values.price_per_user
          };
        }

        await axios.patch(`${process.env.REACT_APP_API_URL}/sales/customer/${client.customer_id}`, payload);
        toast.success(t('commerce.toast.edited'));
        await fetchCustomers();
        // Keep modal open and updated
      } catch (error) {
        console.error('Error updating client:', error);
        toast.error(t('commerce.toast.error'));
      } finally {
        setIsSubmitting(false);
      }
    },
    validationSchema: getValidationSchema(activeTab)
  });

  // Reset form when tab changes (discard unsaved changes)
  const handleTabChange = (event, newValue) => {
    formik.resetForm();
    setActiveTab(newValue);
  };

  const fetchCp = useCallback(
    async id_cp => {
      console.log('fetchCp called with:', id_cp, 'Type:', typeof id_cp);
      if (!id_cp) return;
      try {
        const url = `${process.env.REACT_APP_API_URL}/cp/${id_cp}`;
        console.log('Fetching CP from URL:', url);
        const response = await axios.get(url);
        const data = response.data;
        // Try to find state in known locations (d_estado, cpfind.d_estado, or just estado)
        const state = data?.cpfind?.d_estado || data?.d_estado || data?.estado;

        console.log('Parsed state:', state);

        if (state) {
          console.log('✅ fetchCp - State found:', state);
          setAddressState(state);
          formik.setFieldValue('address.state', state);
          console.log('✅ fetchCp - Updated formik.values.address.state to:', state);
          setIsValidCp(true);
        } else {
          // Fallback if data exists but no state (unlikely if valid)
          console.warn('State not found in response:', data);
          toast.error(t('commerce.toast.cpNotFound'));
          setAddressState('');
          formik.setFieldValue('address.state', '');
          setIsValidCp(false);
        }
      } catch (error) {
        console.error('Error fetching postal code details:', error);
        toast.error(t('commerce.toast.cpNotFound'));
        setAddressState('');
        formik.setFieldValue('address.state', '');
        setIsValidCp(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  // Initialize addressState when modal opens with existing client data
  useEffect(() => {
    if (isOpen && client?.address?.state) {
      setAddressState(client.address.state);
    }
  }, [isOpen, client]);

  useEffect(() => {
    const cp = formik.values.address.postal_code;
    if (cp && cp.length === 5) {
      fetchCp(cp);
    } else if (cp && cp.length > 0) {
      setIsValidCp(false); // Invalid length
      setAddressState('');
    } else {
      setIsValidCp(true); // Empty is handled by Yup required
      setAddressState('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.address.postal_code]);

  if (!isOpen || !client) return null;

  const handleInPersonVisitChange = e => {
    formik.setFieldValue('inPersonVisit', e.target.checked);
  };

  const handleCheckboxChange = e => {
    const isChecked = e.target.checked;
    formik.setFieldValue('bc_is_oc', isChecked);
    if (isChecked) {
      formik.setFieldValue('oc_first_name', formik.values.bc_first_name);
      formik.setFieldValue('oc_last_name', formik.values.bc_last_name);
      formik.setFieldValue('oc_email', formik.values.bc_email);
    } else {
      formik.setFieldValue('oc_first_name', '');
      formik.setFieldValue('oc_email', '');
    }
  };

  const handleResendQuote = async () => {
    // Logic to resend quote
    console.log('Resending quote...');
    // Implement API call here when ready
    toast.info('Función de reenviar cotización en desarrollo');
  };

  const canResend = ['quote_sent', 'cup_sent'].includes(client?.stage); // Adjust status codes as needed

  return (
    <Modal onClose={onClose} open={isOpen}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          left: '50%',
          maxHeight: '90vh',
          maxWidth: 900,
          outline: 'none',
          overflowY: 'auto',
          p: 0,
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%'
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2
          }}
        >
          <Typography variant="h6">
            {t('commerce.clientName')}: {client.name}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', height: 500 }}>
          <Box sx={{ borderColor: 'divider', borderRight: 1, pt: 2, width: 200 }}>
            <Tabs
              onChange={handleTabChange}
              orientation="vertical"
              sx={{ borderColor: 'divider', borderRight: 1 }}
              TabIndicatorProps={{
                style: {
                  backgroundColor: '#D97706',
                  left: 0,
                  width: '4px'
                }
              }}
              value={activeTab}
              variant="scrollable"
            >
              <Tab label={t('commerce.modal.general')} sx={{ alignItems: 'flex-start', color: activeTab === 0 ? '#D97706' : 'inherit' }} />
              <Tab label={t('commerce.modal.fiscal')} sx={{ alignItems: 'flex-start', color: activeTab === 1 ? '#D97706' : 'inherit' }} />
              <Tab label={t('commerce.modal.contact')} sx={{ alignItems: 'flex-start', color: activeTab === 2 ? '#D97706' : 'inherit' }} />
              <Tab
                disabled={client?.stage !== 'quote-created'}
                label={t('commerce.modal.commercial')}
                sx={{ alignItems: 'flex-start', color: activeTab === 3 ? '#D97706' : 'inherit' }}
              />
            </Tabs>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 3 }}>
            <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, pt: 2 }}>
                {activeTab === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={8}>
                          <TextField
                            error={Boolean(formik.touched.name && formik.errors.name)}
                            fullWidth
                            helperText={formik.touched.name && formik.errors.name}
                            InputLabelProps={{ shrink: true }}
                            label={t('commerce.registrationForm.name')}
                            {...formik.getFieldProps('name')}
                          />
                        </Grid>
                        <Grid alignItems="center" display="flex" item xs={4}>
                          <FormControlLabel
                            control={<Checkbox checked={formik.values.inPersonVisit} onChange={handleInPersonVisitChange} />}
                            label={t('commerce.registrationForm.inPersonVisit')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.addressLine1')}
                        {...formik.getFieldProps('address.address_line_1')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.addressLine2')}
                        {...formik.getFieldProps('address.address_line_2')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.neighborhood')}
                        {...formik.getFieldProps('address.neighborhood')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.city')}
                        {...formik.getFieldProps('address.city')}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.postalCode')}
                        {...formik.getFieldProps('address.postal_code')}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        disabled
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.state')}
                        value={addressState || ''}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Select disabled fullWidth value={formik.values.address.country_code}>
                        <MenuItem value="MEX">México</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>
                )}

                {activeTab === 1 && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.rfc')}
                        {...formik.getFieldProps('tax_info.rfc')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.taxName')}
                        {...formik.getFieldProps('tax_info.tax_name')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Select
                        displayEmpty
                        fullWidth
                        onChange={e => formik.setFieldValue('tax_info.tax_regime', e.target.value)}
                        value={formik.values.tax_info.tax_regime}
                      >
                        <MenuItem disabled value="">
                          {t('commerce.registrationForm.taxRegime')}
                        </MenuItem>
                        {taxRegimeOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.taxPostalCode')}
                        {...formik.getFieldProps('tax_info.postal_code')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Select
                        displayEmpty
                        fullWidth
                        onChange={e => formik.setFieldValue('tax_info.cfdi_use_code', e.target.value)}
                        value={formik.values.tax_info.cfdi_use_code}
                      >
                        <MenuItem disabled value="">
                          {t('commerce.registrationForm.cfdiUse')}
                        </MenuItem>
                        {cfdiUseOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                )}

                {activeTab === 2 && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.bcName')}
                        {...formik.getFieldProps('bc_first_name')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.bcLastName')}
                        {...formik.getFieldProps('bc_last_name')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.bcEmail')}
                        {...formik.getFieldProps('bc_email')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={formik.values.bc_is_oc} onChange={handleCheckboxChange} />}
                        label={t('commerce.registrationForm.sameAsGeneralInfo')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.ocName')}
                        {...formik.getFieldProps('oc_first_name')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.ocLastName')}
                        {...formik.getFieldProps('oc_last_name')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.ocEmail')}
                        {...formik.getFieldProps('oc_email')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.category')}
                        onChange={e => formik.setFieldValue('category', e.target.value)}
                        select
                        value={formik.values.category}
                      >
                        <MenuItem disabled value="">
                          {t('commerce.registrationForm.category')}
                        </MenuItem>
                        <MenuItem value="residential">{t('commerce.categoryOptions.residential')}</MenuItem>
                        <MenuItem value="commercial">{t('commerce.categoryOptions.commercial')}</MenuItem>
                        <MenuItem value="industrial">{t('commerce.categoryOptions.industrial')}</MenuItem>
                        <MenuItem value="parking-lots">{t('commerce.categoryOptions.parking')}</MenuItem>
                        <MenuItem value="clubs-and-gyms">{t('commerce.categoryOptions.clubsAndGyms')}</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                )}

                {activeTab === 3 && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.plan')}
                        select
                        {...formik.getFieldProps('plan')}
                      >
                        <MenuItem value={1}>{t('commerce.modal.planOptions.initial')}</MenuItem>
                        <MenuItem value={2}>{t('commerce.modal.planOptions.convenient')}</MenuItem>
                        <MenuItem value={3}>{t('commerce.modal.planOptions.efficient')}</MenuItem>
                        <MenuItem value={4}>{t('commerce.modal.planOptions.intelligent')}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.pricePerUser')}
                        type="number"
                        {...formik.getFieldProps('price_per_user')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.accessPoints')}
                        type="number"
                        {...formik.getFieldProps('number_of_access_points')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        disabled
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.total')}
                        value={
                          formik.values.price_per_user && formik.values.number_of_users
                            ? `$${(Number(formik.values.price_per_user) * Number(formik.values.number_of_users)).toLocaleString()}`
                            : '$0'
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.users')}
                        type="number"
                        {...formik.getFieldProps('number_of_users')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label={t('commerce.registrationForm.bridges')}
                        type="number"
                        {...formik.getFieldProps('number_of_bridges')}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2 }}>
                <Button onClick={onClose} sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary }} variant="outlined">
                  {t('commerce.buttons.close')}
                </Button>
                {activeTab === 3 && (
                  <Button
                    disabled={!canResend}
                    onClick={handleResendQuote}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#D5D5D5'
                      },
                      backgroundColor: canResend ? '#E0E0E0' : theme.palette.action.disabledBackground, // Light gray
                      color: canResend ? theme.palette.text.primary : theme.palette.action.disabled
                    }}
                    variant="contained"
                  >
                    {t('commerce.buttons.resend')}
                  </Button>
                )}
                <Button
                  disabled={!formik.dirty || isSubmitting || !isValidCp}
                  sx={{
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled
                    },
                    '&:hover': {
                      backgroundColor: '#454F5B' // Slightly lighter on hover
                    },
                    backgroundColor: '#212B36', // Hardcoded dark color for contrast
                    color: '#ffffff' // Explicit white text
                  }}
                  type="submit"
                  variant="contained"
                >
                  {t('commerce.buttons.update')}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default ClientEditModal;
